// Pakete im Aivy OS. Reine Helfer ohne React und ohne Netz für die
// Payment-Seite, die Kundenseite und die Migrations-Übersicht.
//
// Zahlen und Verfügbarkeiten kommen nur aus dem Katalog
// (src/constants/pricing-catalog.js, Kopie aus dem Dashboard). Zählregeln und
// Payment-Auswahl kommen aus src/helper/pricing-mirror, damit CS dieselben
// Zahlen sieht wie der Kunde. Neue Regeln gehören ins Dashboard.
import {
  AVAILABILITY,
  CUSTOM_PLAN,
  MANAGED_PRODUCTS,
  PLANS,
  PLAN_ORDER,
  PRODUCT_AVAILABILITY,
  UNLIMITED,
  deriveTalentStorage,
  isUnlimitedCount,
  normalizePlanId,
  presetProducts,
  toStoredCount
} from '../constants/pricing-catalog'
import {
  LEGACY_SCHEMA_PRODUCTS,
  productTitle,
  sortProducts
} from '../constants/products'
import { resolvePlanLimits, toCount } from './pricing-mirror/limits'
import {
  isCreditPayment,
  planOf,
  selectPayments
} from './pricing-mirror/payment-selection'
import { countActiveCareers } from './pricing-mirror/usage/careers'
import { countSeats } from './pricing-mirror/usage/seats'
import {
  lockedIdsFor,
  lockedUnitsFor,
  rankTalentStorage
} from './pricing-mirror/usage/talent-storage'

export { UNLIMITED }

// ---------------------------------------------------------------------------
// Pakete und Anzeige
// ---------------------------------------------------------------------------

export const PLAN_NAMES = {
  start: 'Start',
  grow: 'Growth',
  scale: 'Scale',
  unlimited: 'Unlimited',
  custom: 'Individuell'
}

export const UNKNOWN_PLAN_LABEL = 'Unbekannter Plan'
export const NO_PLAN_LABEL = 'Kein Paket'

// Auswahl auf der Payment-Seite, in dieser Reihenfolge
export const PLAN_CHOICES = [...PLAN_ORDER, CUSTOM_PLAN]

// Gilt nur für Plan-Payments. Erst ab UNLIMITED ist eine Menge unbegrenzt.
export const isUnlimited = (n) => isUnlimitedCount(n)

export const planName = (planId) => PLAN_NAMES[normalizePlanId(planId)] || null

/**
 * Gespeicherter plan-Wert für die Anzeige. Ein unbekannter Wert zählt im
 * Dashboard als Altvertrag (fail-open), das OS zeigt ihn als „Unbekannter
 * Plan".
 *
 * @returns {{ kind: 'none'|'plan'|'unknown', planId: string|null,
 *   label: string, raw: string|null }}
 */
export const describePlanValue = (raw) => {
  if (raw === null || raw === undefined || raw === '') {
    return { kind: 'none', planId: null, label: NO_PLAN_LABEL, raw: null }
  }

  const planId = normalizePlanId(raw)
  if (!planId) {
    return {
      kind: 'unknown',
      planId: null,
      label: UNKNOWN_PLAN_LABEL,
      raw: String(raw)
    }
  }

  return { kind: 'plan', planId, label: PLAN_NAMES[planId], raw: String(raw) }
}

export const PAYMENT_KINDS = {
  CREDITS: 'credits',
  TRIAL: 'trial',
  PLAN: 'plan',
  UNKNOWN_PLAN: 'unknown_plan',
  LEGACY: 'legacy'
}

/**
 * Art eines Payments für Badges. Ein Plan an einem Trial zählt nicht, das
 * Payment bleibt ein Trial.
 */
export const paymentKind = (payment) => {
  if (isCreditPayment(payment)) return PAYMENT_KINDS.CREDITS
  if (payment?.is_trial) return PAYMENT_KINDS.TRIAL
  if (planOf(payment)) return PAYMENT_KINDS.PLAN

  const { kind } = describePlanValue(payment?.plan)
  return kind === 'unknown' ? PAYMENT_KINDS.UNKNOWN_PLAN : PAYMENT_KINDS.LEGACY
}

// ---------------------------------------------------------------------------
// Produkte
// ---------------------------------------------------------------------------

/**
 * Produkte, die das Schema dieser Stage kennt. Ohne Ergebnis der Probe nur
 * die Werte, die jede Stage schon vor dem Preismodell kannte.
 *
 * @param {{ products?: string[]|null }} [capabilities] aus useSchemaCapabilities
 */
export const availableProductsFor = (capabilities) =>
  Array.isArray(capabilities?.products)
    ? capabilities.products
    : LEGACY_SCHEMA_PRODUCTS

const isAvailableIn = (available) => (product) =>
  !Array.isArray(available) || available.includes(product)

const toList = (products) =>
  Array.isArray(products) ? products.filter(Boolean) : []

const isCatalogPlan = (planId) => PLAN_ORDER.includes(planId)

/**
 * Produkte nach einem Paketwechsel. Ersetzt nur verwaltete Produkte
 * (MANAGED_PRODUCTS). Nicht verwaltete wie FAKINGMODULE, SKILLCHECK,
 * CUSTOMIZE_UI oder TEAM_ANALYSIS_BASIC bleiben stehen, auch wenn das Grid
 * sie nicht zeigt. Gebuchte Zusatzoptionen bleiben, solange das Zielpaket sie
 * als Zusatz anbietet. Produkte, die das Schema nicht kennt, kommen nicht
 * dazu. Für custom und unbekannte Pakete bleibt alles, wie es ist.
 *
 * @param {string[]} current Produkte des Formulars
 * @param {string} planId Zielpaket
 * @param {{ available?: string[]|null }} [options] aus availableProductsFor
 * @returns {string[]} in Enum-Reihenfolge
 */
export const mergePresetProducts = (current, planId, { available } = {}) => {
  const list = toList(current)
  const target = normalizePlanId(planId)
  if (!isCatalogPlan(target)) return sortProducts(list)

  const unmanaged = list.filter(
    (product) => !MANAGED_PRODUCTS.includes(product)
  )
  const keptAddons = list.filter(
    (product) =>
      MANAGED_PRODUCTS.includes(product) &&
      PRODUCT_AVAILABILITY[product][target] === AVAILABILITY.ADDON
  )
  const preset = presetProducts(target).filter(isAvailableIn(available))

  return sortProducts([...unmanaged, ...preset, ...keptAddons])
}

// ---------------------------------------------------------------------------
// Mengen
// ---------------------------------------------------------------------------

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

/**
 * Talentspeicher für ein Paket und eine Stellenzahl, +50 je Zusatzstelle wie
 * LP speicher(). Ohne Stellenzahl gilt das Paket. Unlimited ergibt
 * UNLIMITED. custom und unbekannte Pakete ergeben null, dann gibt es keinen
 * Vorschlag.
 *
 * @returns {number|null} Wert zum Speichern
 */
export const storageFor = (planId, careerCount) => {
  const target = normalizePlanId(planId)
  if (!isCatalogPlan(target)) return null

  const careers = toNumberOrNull(careerCount)
  return toStoredCount(
    deriveTalentStorage(target, careers === null ? undefined : careers)
  )
}

/**
 * Wendet ein Paket auf die Formularwerte an.
 * - start, grow, scale setzen Stellen, Zugänge und Talentspeicher auf das
 *   Paket, Zusatzstellen und Zusatzzugänge fallen also weg
 * - unlimited setzt alle drei auf UNLIMITED
 * - custom setzt nur plan, Zahlen und Produkte bleiben
 * - null oder leer heißt „Kein Paket" (Trial oder Altvertrag), nur plan wird
 *   geleert
 * Paket-Payments sind nie Trials. Ein unbekanntes Paket wirft.
 *
 * @param {object} values Formularwerte (career_count, seat_count,
 *   spaces_count, products, is_trial, plan, ...)
 * @param {string|null} planId
 * @param {{ available?: string[]|null }} [options]
 */
export const applyPreset = (values, planId, { available } = {}) => {
  const current = values || {}
  if (planId === null || planId === undefined || planId === '') {
    return { ...current, plan: null }
  }

  const target = normalizePlanId(planId)
  if (!target) throw new Error(`Unbekanntes Paket ${planId}`)

  if (target === CUSTOM_PLAN) {
    return { ...current, plan: CUSTOM_PLAN, is_trial: false }
  }

  const { limits } = PLANS[target]

  return {
    ...current,
    plan: target,
    is_trial: false,
    career_count: toStoredCount(limits.careers),
    seat_count: toStoredCount(limits.seats),
    spaces_count: toStoredCount(limits.talentStorage),
    products: mergePresetProducts(current.products, target, { available })
  }
}

// ---------------------------------------------------------------------------
// Abweichungen vom Paket
// ---------------------------------------------------------------------------

const NUMBER_FORMAT = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0
})

export const formatCount = (n) =>
  isUnlimited(n) || n === Infinity ? 'unbegrenzt' : NUMBER_FORMAT.format(n)

const plural = (n, one, other) => `${formatCount(n)} ${n === 1 ? one : other}`

const COUNT_FIELDS = {
  careers: {
    field: 'career_count',
    one: 'Stelle',
    other: 'Stellen',
    addonOne: 'Zusatzstelle',
    addonOther: 'Zusatzstellen',
    unlimited: 'Unlimited erwartet unbegrenzte Stellen'
  },
  seats: {
    field: 'seat_count',
    one: 'Zugang',
    other: 'Zugänge',
    addonOne: 'Zusatzzugang',
    addonOther: 'Zusatzzugänge',
    unlimited: 'Unlimited erwartet unbegrenzte Zugänge'
  }
}

const info = (field, message) => ({ kind: 'info', field, message })
const deviation = (field, message) => ({ kind: 'deviation', field, message })

// Stellen oder Zugänge gegen Paket und Maximum. 0 und leer heißen keine
// Angabe (das Dashboard nimmt dann den Paketwert), dazu schweigt der Diff.
const countItems = (resource, value, plan) => {
  const def = COUNT_FIELDS[resource]
  const included = plan.limits[resource]
  const max = plan.max[resource]
  if (value === null || value <= 0) return []

  if (value > max) {
    return [
      deviation(
        def.field,
        `Über Paketmaximum (${plural(max, def.one, def.other)})`
      )
    ]
  }
  if (value < included) {
    return [
      deviation(
        def.field,
        `Weniger ${def.other} als im Paket (${formatCount(value)} statt ${formatCount(included)})`
      )
    ]
  }
  if (value > included) {
    // Growth und Scale erlauben unbegrenzte Zugänge (max Infinity). 999999
    // minus Paketwert wäre keine sinnvolle Zahl an Zusatzzugängen.
    if (isUnlimited(value)) {
      return [info(def.field, `Unbegrenzt viele ${def.other}`)]
    }
    return [
      info(def.field, plural(value - included, def.addonOne, def.addonOther))
    ]
  }
  return []
}

const productItems = (products, planId, available) => {
  const selected = new Set(toList(products))
  const name = PLAN_NAMES[planId]

  return MANAGED_PRODUCTS.filter(isAvailableIn(available)).flatMap(
    (product) => {
      const availability = PRODUCT_AVAILABILITY[product][planId]
      const title = productTitle(product)
      const isSelected = selected.has(product)

      if (availability === AVAILABILITY.YES && !isSelected) {
        return [deviation(product, `${title} fehlt`)]
      }
      if (availability === AVAILABILITY.ADDON && isSelected) {
        return [info(product, `Zusatzoption ${title}`)]
      }
      if (availability === AVAILABILITY.NO && isSelected) {
        return [deviation(product, `${title} ist nicht in ${name} enthalten`)]
      }
      return []
    }
  )
}

/**
 * Wie weit die Werte vom Paket abweichen. info sind erwartbare Zusätze
 * (Zusatzstellen, Zusatzzugänge, Zusatzoptionen), deviation echte
 * Abweichungen. customized ist true, sobald es eine deviation gibt, das ist
 * das Badge „Angepasst". custom, „Kein Paket" und unbekannte Pakete haben
 * keine Vorlage und ergeben eine leere Liste.
 *
 * @returns {{ customized: boolean, items: { kind: 'info'|'deviation',
 *   field: string, message: string }[] }}
 */
export const derivePresetDiff = (values, planId, { available } = {}) => {
  const target = normalizePlanId(planId)
  if (!isCatalogPlan(target)) return { customized: false, items: [] }

  const plan = PLANS[target]
  const careers = toNumberOrNull(values?.career_count)
  const seats = toNumberOrNull(values?.seat_count)
  const storage = toNumberOrNull(values?.spaces_count)
  const items = []

  if (target === 'unlimited') {
    if (careers !== null && !isUnlimited(careers)) {
      items.push(deviation('career_count', COUNT_FIELDS.careers.unlimited))
    }
    if (seats !== null && !isUnlimited(seats)) {
      items.push(deviation('seat_count', COUNT_FIELDS.seats.unlimited))
    }
    if (storage !== null && !isUnlimited(storage)) {
      items.push(
        deviation(
          'spaces_count',
          'Unlimited erwartet unbegrenzten Talentspeicher'
        )
      )
    }
  } else {
    items.push(...countItems('careers', careers, plan))
    items.push(...countItems('seats', seats, plan))

    const expected = storageFor(
      target,
      careers !== null && careers > 0 ? careers : undefined
    )
    if (storage !== null && storage > 0 && storage !== expected) {
      items.push(
        deviation(
          'spaces_count',
          `Talentspeicher ${formatCount(storage)} statt ${formatCount(expected)}`
        )
      )
    }
  }

  items.push(...productItems(values?.products, target, available))

  return {
    customized: items.some(({ kind }) => kind === 'deviation'),
    items
  }
}

// ---------------------------------------------------------------------------
// Listenpreis und Empfehlung (nur Hinweis, nie automatisch gespeichert)
// ---------------------------------------------------------------------------

// Listenpreise der LP (src/components/pricing/pricingModel.js, origin/production
// 23eb713), netto je Monat im Jahresvertrag. Der Katalog führt keine
// Grundpreise, weil das Dashboard keine Preise zeigt. Ändert die LP ihre
// Preise, hier nachziehen.
export const LIST_PRICES = {
  base: { start: 290, grow: 1490, scale: 4490, unlimited: 8490 },
  flexMonthly: { start: 490 },
  atsPluginStart: 290
}

// LP NAEHE. Ist das nächstgrößere Paket höchstens so viel teurer, empfiehlt
// die LP es statt des günstigsten.
export const RECOMMEND_UPGRADE_WITHIN = 250

const addonsOf = (included, value) =>
  Number.isFinite(included) ? Math.max(0, value - included) : 0

/**
 * Listenpreis als Hinweis für CS, rechnet wie LP kosten() im Jahresvertrag.
 * Zusatzoptionen ohne Listenpreis (Custom API, FR, NL) stehen in onRequest.
 * custom, unbekannte Pakete und unbegrenzte Mengen in Start, Growth oder
 * Scale ergeben null.
 *
 * @returns {{ planId: string, monthly: number, flexMonthly: number|null,
 *   breakdown: { label: string, amount: number }[], onRequest: string[] }|null}
 */
export const listPrice = (
  planId,
  { careerCount, seatCount, products } = {}
) => {
  const target = normalizePlanId(planId)
  if (!isCatalogPlan(target)) return null

  const plan = PLANS[target]
  const careers = toNumberOrNull(careerCount) ?? plan.limits.careers
  const seats = toNumberOrNull(seatCount) ?? plan.limits.seats
  const finite = target !== 'unlimited'
  if (finite && (isUnlimited(careers) || isUnlimited(seats))) return null

  const breakdown = [
    {
      label: `Grundpreis ${PLAN_NAMES[target]}`,
      amount: LIST_PRICES.base[target]
    }
  ]

  const addonCareers = finite ? addonsOf(plan.limits.careers, careers) : 0
  if (addonCareers > 0) {
    breakdown.push({
      label: plural(addonCareers, 'Zusatzstelle', 'Zusatzstellen'),
      amount: addonCareers * plan.addonPrice.career
    })
  }

  const addonSeats = finite ? addonsOf(plan.limits.seats, seats) : 0
  if (addonSeats > 0) {
    breakdown.push({
      label: plural(addonSeats, 'Zusatzzugang', 'Zusatzzugänge'),
      amount: addonSeats * plan.addonPrice.seat
    })
  }

  const onRequest = []
  toList(products).forEach((product) => {
    if (PRODUCT_AVAILABILITY[product]?.[target] !== AVAILABILITY.ADDON) return
    if (target === 'start' && product === 'BROWSERPLUGIN') {
      breakdown.push({
        label: productTitle(product),
        amount: LIST_PRICES.atsPluginStart
      })
      return
    }
    onRequest.push(product)
  })

  const monthly = breakdown.reduce((sum, { amount }) => sum + amount, 0)
  const flex = LIST_PRICES.flexMonthly[target]

  return {
    planId: target,
    monthly,
    flexMonthly:
      typeof flex === 'number'
        ? monthly - LIST_PRICES.base[target] + flex
        : null,
    breakdown,
    onRequest: sortProducts(onRequest)
  }
}

// Wie LP gueltig(), Limits sind inklusiv
const fitsPlan = (planId, { careers, seats, storage }) => {
  const plan = PLANS[planId]
  return (
    careers <= plan.max.careers &&
    seats <= plan.max.seats &&
    storage <= deriveTalentStorage(planId, careers)
  )
}

const usageCount = (value) => {
  const n = toCount(value)
  return n === null ? 0 : n
}

/**
 * Passendes Paket zur heutigen Nutzung, wie die Empfehlung der LP
 * (empfehlung() in pricingModel.js). Unter den Paketen, in die die Nutzung
 * passt, gewinnt das günstigste im Jahresvertrag. Ist das nächstgrößere
 * höchstens RECOMMEND_UPGRADE_WITHIN teurer, gewinnt dieses. Passt nichts,
 * ist es Unlimited.
 *
 * @param {{ careers: number, seats: number, storage: number }} usage aktive
 *   Stellen, Zugänge und Personen im Talentspeicher
 * @returns {{ planId: string, addonCareers: number, addonSeats: number,
 *   monthly: number }}
 */
export const recommendPlan = ({ careers, seats, storage } = {}) => {
  const usage = {
    careers: usageCount(careers),
    seats: usageCount(seats),
    storage: usageCount(storage)
  }

  const result = (planId) => {
    const plan = PLANS[planId]
    const finite = planId !== 'unlimited'
    const price = listPrice(planId, {
      careerCount: Math.max(usage.careers, finite ? plan.limits.careers : 0),
      seatCount: Math.max(usage.seats, finite ? plan.limits.seats : 0)
    })

    return {
      planId,
      addonCareers: finite ? addonsOf(plan.limits.careers, usage.careers) : 0,
      addonSeats: finite ? addonsOf(plan.limits.seats, usage.seats) : 0,
      monthly: price.monthly
    }
  }

  const options = PLAN_ORDER.filter((planId) => fitsPlan(planId, usage)).map(
    result
  )
  if (!options.length) return result('unlimited')

  let best = options.reduce((a, b) => (b.monthly < a.monthly ? b : a))
  const next = options.find(
    (option) =>
      PLAN_ORDER.indexOf(option.planId) > PLAN_ORDER.indexOf(best.planId)
  )
  if (next && next.monthly <= best.monthly + RECOMMEND_UPGRADE_WITHIN) {
    best = next
  }

  return best
}

// ---------------------------------------------------------------------------
// Nutzung
// ---------------------------------------------------------------------------

/**
 * Nutzung aus den rohen Listen, mit denselben Regeln wie das Dashboard.
 * Talentspeicher zählt Personen, nicht Spaces.
 *
 * @param {{ careers: object[], spaces: object[], members: object[] }} lists
 *   aktive Stellen, Talent-Spaces (SELECT, TALENTPOOL, ATTRACT) und
 *   EMPLOYEE-Spaces
 */
export const summarizeUsage = ({ careers, spaces, members } = {}) => {
  const index = rankTalentStorage(spaces)

  return {
    careers: countActiveCareers(careers),
    seats: countSeats(members),
    talentStorage: index.used,
    index
  }
}

const validLimit = (limit) =>
  typeof limit === 'number' &&
  Number.isFinite(limit) &&
  limit >= 0 &&
  !isUnlimited(limit)

/**
 * Personen, die bei diesem Talentspeicher gesperrt wären. Unbegrenzt,
 * unbekannt oder ungültig sperrt nichts (wie lockedIdsFor im Dashboard).
 */
export const lockedTalentsFor = (index, limit) =>
  validLimit(limit) ? lockedUnitsFor(index, limit).size : 0

// Zeilen der Talentliste, die gesperrt wären (alle Spaces gesperrter Personen)
export const lockedTalentSpacesFor = (index, limit) =>
  validLimit(limit) ? lockedIdsFor(index, limit).size : 0

const legacyLimit = (value) => {
  const n = toCount(value)
  if (n === null || n <= 0) return null
  return isUnlimited(n) ? Infinity : n
}

/**
 * Limits eines Payments für Meter. Plan-Payments rechnen wie das Dashboard in
 * enforced (Payment-Wert gewinnt, 0 und leer heißen Paketwert, ab UNLIMITED
 * unbegrenzt). Alle anderen zeigen ihre Rohwerte, 0 und leer heißen
 * unbekannt. Unbegrenzt ist Infinity.
 *
 * @returns {{ planId: string|null, careers: number|null, seats: number|null,
 *   talentStorage: number|null }}
 */
export const effectiveLimitsOf = (payment) => {
  const planId = planOf(payment)
  if (!planId) {
    return {
      planId: null,
      careers: legacyLimit(payment?.career_count),
      seats: legacyLimit(payment?.seat_count),
      talentStorage: legacyLimit(payment?.spaces_count)
    }
  }

  const limits = resolvePlanLimits({ planId, payment })
  return {
    planId,
    careers: limits.careers.limit,
    seats: limits.seats.limit,
    talentStorage: limits.talentStorage.limit
  }
}

// ---------------------------------------------------------------------------
// Wirkung einer neuen Plan-Zahlung ohne Pricing-Sperre
// ---------------------------------------------------------------------------

const DRAFT_ID = '__draft_payment__'

/**
 * Payments des Kunden, wie sie nach dem Speichern aussehen. Mit id ersetzt
 * der Entwurf das gespeicherte Payment (übrige Felder bleiben), ohne id kommt
 * er als neuestes dazu.
 *
 * @param {{ payments: object[], draft: object|null, now?: Date }} params
 * @returns {object[]}
 */
export const paymentsAfterSave = ({
  payments,
  draft,
  now = new Date()
} = {}) => {
  const list = Array.isArray(payments) ? payments.filter(Boolean) : []
  if (!draft) return list

  const existing = draft.id ? list.find(({ id }) => id === draft.id) : null
  const candidate = {
    ...(existing || {}),
    ...draft,
    id: draft.id || DRAFT_ID,
    createdAt: draft.createdAt || existing?.createdAt || now.toISOString()
  }
  return [...list.filter(({ id }) => id !== candidate.id), candidate]
}

/**
 * Ob ein Plan-Payment schon ohne Pricing-Sperre wirkt. Das ist der Fall,
 * wenn der Kunde kein Payment ohne Paket hat oder das neueste davon ein Trial
 * ist (konvertierter Trial, Neukunde). Ein älteres Altpayment vor dem Trial
 * zählt dann nicht mehr. Dann rechnet das Dashboard mit genau
 * diesem Payment und der alten Logik, also mit seinem Seat-Limit und den fünf
 * bisherigen Sperren nach seinen Produkten. Die Prüfung läuft über
 * selectPayments aus dem Dashboard.
 *
 * @param {{ payments: object[], draft: object }} params draft sind die Werte
 *   der neuen oder bearbeiteten Zahlung. Mit id ersetzt er das gespeicherte
 *   Payment, ohne createdAt gilt er als neuestes.
 * @returns {{ appliesNow: boolean, messages: string[] }}
 */
export const planAppliesImmediately = ({ payments, draft } = {}) => {
  if (!draft || !planOf(draft)) return { appliesNow: false, messages: [] }

  const after = paymentsAfterSave({ payments, draft })
  const candidateId = draft.id || DRAFT_ID
  const { legacyModePayment } = selectPayments(after)
  const appliesNow = legacyModePayment?.id === candidateId

  return {
    appliesNow,
    messages: appliesNow
      ? [
          'Das neueste Payment ohne Paket ist ein Trial oder es gibt keins. Deshalb gilt diese Zahlung schon ab dem Speichern, auch bei ausgeschalteter Pricing-Sperre.',
          'Das Dashboard begrenzt dann die Zugänge auf diese Zahlung und schaltet Orientierungsmodul, Weiterleitung, Kreuzmatching, Integrationen und DSGVO-Automatisierung nach ihren Produkten frei.'
        ]
      : []
  }
}

// ---------------------------------------------------------------------------
// DSGVO-Automatisierung
// ---------------------------------------------------------------------------

// Werte von automations.delete_spaces, bei denen gelöscht wird. Fehlt der
// Wert (independent), ist die Automatisierung aus.
export const DELETE_AUTOMATION_OPTIONS = ['all', 'anonym']

const DELETE_OPTION_LABELS = {
  all: 'alle Daten',
  anonym: 'anonymisiert'
}

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * partner.app_settings.automations ist AWSJSON und kommt als String, manchmal
 * doppelt kodiert oder schon als Objekt. Kaputtes JSON ergibt {}.
 */
export const parseAutomations = (raw) => {
  let value = raw
  try {
    for (let i = 0; i < 2 && typeof value === 'string'; i++) {
      value = JSON.parse(value)
    }
  } catch (_) {
    return {}
  }
  return isPlainObject(value) ? value : {}
}

/**
 * Aktive Lösch-Automatisierung des Partners oder null.
 *
 * @returns {{ option: 'all'|'anonym', months: number }|null}
 */
export const deleteAutomationOf = (partner) => {
  const automations = parseAutomations(partner?.app_settings?.automations)
  const option = automations.delete_spaces
  if (!DELETE_AUTOMATION_OPTIONS.includes(option)) return null

  const months = Number(automations.automation_soft_delete_after_months)
  return {
    option,
    months: Number.isFinite(months) && months > 0 ? months : 6
  }
}

const dateLabel = (iso) =>
  new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

// Wie checkIfPaymentHasActiveAutomations in inviteTalent. Ein Payment trägt
// die Löschung, solange es AUTOMATIONS hat und expires_at in der Zukunft
// liegt. Ohne gültiges expires_at trägt es nichts.
export const coversAutomation = (payment, now = new Date()) => {
  if (!payment || !toList(payment.products).includes('AUTOMATIONS')) {
    return false
  }
  const expires = new Date(payment.expires_at ?? NaN)
  return !Number.isNaN(expires.getTime()) && expires > now
}

const latestCoverage = (payments, now) =>
  payments
    .filter((payment) => coversAutomation(payment, now))
    .map(({ expires_at }) => new Date(expires_at).toISOString())
    .sort((a, b) => new Date(b) - new Date(a))[0] || null

/**
 * Ob vor und nach dem Speichern irgendein Payment die Löschung trägt.
 *
 * @param {{ payments: object[], draft: { id?: string, products: string[],
 *   expires_at: Date|string|null }, now?: Date }} params
 * @returns {{ coveredBefore: boolean, coveredAfter: boolean,
 *   draftCovers: boolean, coveredUntil: string|null }} coveredUntil ist das
 *   späteste Vertragsende, das nach dem Speichern noch trägt
 */
export const automationCoverage = ({ payments, draft, now = new Date() }) => {
  const before = Array.isArray(payments) ? payments.filter(Boolean) : []
  const after = paymentsAfterSave({ payments: before, draft, now })

  return {
    coveredBefore: before.some((payment) => coversAutomation(payment, now)),
    coveredAfter: after.some((payment) => coversAutomation(payment, now)),
    draftCovers: coversAutomation(draft, now),
    coveredUntil: latestCoverage(after, now)
  }
}

export const DSGVO_WARNING_KINDS = {
  // Speichern beendet die Löschung, heute läuft sie noch
  STOPS: 'stops',
  // Heute trägt schon kein Payment die Löschung
  STOPPED: 'stopped',
  // Diese Zahlung trägt nicht, ein anderes Payment noch
  COVERED_BY_OTHERS: 'covered_by_others'
}

/**
 * Warnung, wenn die Lösch-Automatisierung des Kunden aktiv ist und diese
 * Zahlung die Löschung nicht selbst trägt. inviteTalent plant Löschungen nur
 * ein, solange irgendein Payment AUTOMATIONS und ein expires_at in der Zukunft
 * hat. Bestätigen muss CS nur, wenn nach dem Speichern kein Payment mehr
 * trägt (requiresConfirmation). Trägt ein anderes Payment, ist es ein
 * Hinweis mit dessen Ende.
 *
 * @param {{ partner: object, payments?: object[], draft: { id?: string,
 *   products: string[], expires_at: Date|string|null }, now?: Date }} params
 *   payments sind alle gespeicherten Payments des Kunden, draft die Zahlung im
 *   Formular (mit id beim Bearbeiten)
 * @returns {{ kind: string, requiresConfirmation: boolean, option: string,
 *   months: number, coveredBefore: boolean, coveredUntil: string|null,
 *   message: string }|null}
 */
export const dsgvoAutomationWarning = ({
  partner,
  payments = [],
  draft = null,
  now = new Date()
} = {}) => {
  const automation = deleteAutomationOf(partner)
  if (!automation) return null

  const coverage = automationCoverage({ payments, draft, now })
  if (coverage.draftCovers) return null

  const messages = [
    `Beim Kunden ist die automatische Löschung von Talenten aktiv (${DELETE_OPTION_LABELS[automation.option]}, nach ${automation.months} Monaten).`,
    toList(draft?.products).includes('AUTOMATIONS')
      ? 'Diese Zahlung enthält DSGVO-Automatisierung, ihr Vertragsende liegt aber nicht in der Zukunft.'
      : 'Diese Zahlung enthält keine DSGVO-Automatisierung.'
  ]
  const base = {
    option: automation.option,
    months: automation.months,
    coveredBefore: coverage.coveredBefore,
    coveredUntil: coverage.coveredUntil
  }

  if (coverage.coveredAfter) {
    messages.push(
      `Die Löschung läuft weiter, weil ein anderes Payment mit DSGVO-Automatisierung noch bis ${dateLabel(coverage.coveredUntil)} gilt. Danach stoppt sie, wenn bis dahin keine Zahlung das Produkt mit einem späteren Vertragsende trägt.`
    )
    return {
      ...base,
      kind: DSGVO_WARNING_KINDS.COVERED_BY_OTHERS,
      requiresConfirmation: false,
      message: messages.join(' ')
    }
  }

  messages.push(
    coverage.coveredBefore
      ? 'Nach dem Speichern trägt kein Payment mehr DSGVO-Automatisierung mit einem Vertragsende in der Zukunft. Das Backend plant für neue Talente dann keine Löschung mehr ein.'
      : 'Schon heute trägt kein Payment DSGVO-Automatisierung mit einem Vertragsende in der Zukunft, das Backend plant für neue Talente keine Löschung ein.',
    'Behalte das Produkt mit einem Vertragsende in der Zukunft als Bestandsschutz oder kläre es vorher mit dem Kunden.'
  )
  return {
    ...base,
    kind: coverage.coveredBefore
      ? DSGVO_WARNING_KINDS.STOPS
      : DSGVO_WARNING_KINDS.STOPPED,
    requiresConfirmation: true,
    message: messages.join(' ')
  }
}
